import React from "react";
import { Theme, alpha } from "@mui/material";
import DSDialog from "../../system-design/DSDialog";
import KlydoModal from "../../Klydos/components/KlydoModal";

// Default changelog window: the last two months.
export const getDefaultChangeLogFromDate = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() - 2);
  return d.toISOString().split("T")[0];
};

// Parse a "records to fetch" text input into a usable limit. Returns null for
// empty / NaN / non-positive input so callers can ignore it. Callers allow the
// empty intermediate (so the field can be cleared and retyped); a valid number
// is clamped to [1, MAX_RECORD_LIMIT] to avoid pulling an unbounded window.
export const MAX_RECORD_LIMIT = 2000;

export const parseRecordLimit = (value: string): number | null => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.min(Math.floor(n), MAX_RECORD_LIMIT);
};

export const changeLogCellSx = {
  fontFamily: "monospace",
  fontSize: "0.875rem",
  padding: "6px 12px",
};

export const changeLogHeaderCellSx = {
  ...changeLogCellSx,
  fontWeight: "bold" as const,
};

export type ChangeLogChipSeverity = "added" | "removed" | "info";

// Theme-aware chip fill/text for changelog chips, adapting to light/dark mode.
export const changeLogChipColors = (
  theme: Theme,
  severity: ChangeLogChipSeverity,
) => {
  const isDark = theme.palette.mode === "dark";
  const p =
    severity === "added"
      ? theme.palette.success
      : severity === "removed"
        ? theme.palette.error
        : theme.palette.info;
  return {
    backgroundColor: alpha(p.main, isDark ? 0.25 : 0.15),
    color: isDark ? p.light : p.dark,
  };
};

// Klydo detail popup used by the changelog dialogs when a chip is clicked.
export const KlydoPreviewDialog: React.FC<{
  klydoId: string;
  onClose: () => void;
}> = ({ klydoId, onClose }) => (
  <DSDialog fullWidth maxWidth="lg" open onClose={onClose}>
    <KlydoModal
      klydoId={klydoId}
      setShowModal={(show: boolean) => {
        if (!show) onClose();
      }}
    />
  </DSDialog>
);
